import React, { useCallback, useRef } from "react"
import { State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { Col, Row } from "react-bootstrap"
import { useDrag, useDrop } from "react-dnd"
import { useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { ReferenceDataType, withReferenceDataState } from "../../globalStates/ReferenceDataHOC"
import { getElementFieldValue } from "../../globalStates/utils"
import SelectField from "../../components/Field/Select"
import TextField from "../../components/Field/Text"
import TextAreaField from "../../components/Field/TextArea"
import FilePicker from "../../components/FilePicker"
import Section from "../../components/Section"
import {
    fieldNameByLang,
    getElementFieldValueWithLanguage,
    makeSetHandlerWithLanguage,
    makeValidationHandlerWithLanguage,
    useLanguageSwitch
} from "../../utils/Language"
import LanguageSwitch from "../languageSwitch"
import { Showrooms } from "../../globalStates/ExhibitorDataTypes"

interface ShowroomsSectionProps {
    showroomsState: State<Showrooms, Validation>
    index: number
    type: string
    onOrderChanged: (productId: string, index: number) => void
    onClickRemove: (productId: string) => void
}

const ShowroomsSection: React.FC<ShowroomsSectionProps & ReferenceDataType> = ({
    showroomsState,
    refDataState,
    index,
    type,
    onOrderChanged,
    onClickRemove
}) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const showroom = useHookstate(showroomsState)
    const isMonoLingual = branding.configuration.monoLingual ? "De" : "En"
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual)
    const sectionRef = useRef<HTMLDivElement>(null)

    const onChangeLogo = useCallback(
        (
            image: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("LOGO", image, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        showroom.logoFileId.set(response.fileId)
                        showroom.logourl.set(response.fileUrl)
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, showroom]
    )

    const onRemoveLogo = useCallback(() => {
        showroom.logoFileId.set("")
        showroom.logourl.set("")
    }, [showroom])

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(showroom.id.get())
    }, [onClickRemove, showroom])

    const [{ isDragging }, drag] = useDrag({
        type: "Product",
        item: { productId: showroom.id.get() },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
        end: (item, monitor) => {
            // const { id: droppedId, originalIndex } = item
            const didDrop = monitor.didDrop()
            if (!didDrop) {
                console.log("!didDrop", item)
            }
        }
    })
    drag(sectionRef)

    const [, drop] = useDrop({
        accept: "Product",
        hover(droppedItem: any) {
            if (droppedItem.productId !== showroom.id.get()) {
                onOrderChanged(droppedItem.productId, index)
            }
        },
        drop(droppedItem: any) {
            onOrderChanged(droppedItem.productId, index)
        }
    })
    drop(sectionRef)

    return (
        <Section
            ref={sectionRef}
            title={
                showroom.title.get() ||
                showroom.titleDe.get() ||
                branding.configuration.showroomsPage.sctShowrooms.titleEmptyShowrooms
            }
            collapsible
            opacity={isDragging ? 0 : 1}
            onClickRemove={onClickRemoveHandler}
            entityState={showroom}
        >
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="companyLogo"
                        label={branding.configuration.showroomsPage.sctShowrooms.lblShowroomsImage}
                        message={branding.configuration.showroomsPage.sctShowrooms.lblShowroomsImageMessage}
                        browseLabel={branding.configuration.showroomsPage.sctShowrooms.btnBrowseFiles}
                        deleteLabel={branding.configuration.showroomsPage.sctShowrooms.btnDeleteImg}
                        placeholderTitle={branding.configuration.showroomsPage.sctShowrooms.hntTitle}
                        placeholderText={branding.configuration.showroomsPage.sctShowrooms.hntProp}
                        value={showroom.logourl.get()}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`showroom_${index}_${type}_language_switch`}
                                name={`showroom_${index}_${type}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={showroomsState}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id={fieldNameByLang("title", currentLanguage)}
                                name={fieldNameByLang("title", currentLanguage)}
                                label={branding.configuration.showroomsPage.sctShowrooms.lblShowroomstitle}
                                placeholder={branding.configuration.showroomsPage.sctShowrooms.hntShowroomstitle}
                                validate={makeValidationHandlerWithLanguage(showroom, "title", currentLanguage)}
                                value={getElementFieldValueWithLanguage(showroom, "title", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(showroom, "title", currentLanguage)}
                                required
                            />
                        </Col>
                        <Col xs="4">
                            <SelectField
                                id="accessMethod"
                                name="accessMethod"
                                label={branding.configuration.showroomsPage.sctShowrooms.lblAccessMethod}
                                placeholder={branding.configuration.showroomsPage.sctShowrooms.hntAccessMethod}
                                options={refDataState.getAccessMethod()}
                                readonly={true}
                                value={getElementFieldValue(showroom, "accessMethod")}
                                onChange={onChangeLanguageHandler}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("shortDescription", currentLanguage)}
                                label={branding.configuration.showroomsPage.sctShowrooms.lblDescription}
                                placeholder={branding.configuration.showroomsPage.sctShowrooms.hntDescription}
                                value={getElementFieldValueWithLanguage(showroom, "shortDescription", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(showroom, "shortDescription", currentLanguage)}
                                limit={200}
                                style={{ height: "80px" }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Section>
    )
}

export default React.memo(withReferenceDataState(ShowroomsSection))
